import { Box, Text } from '@streamelements/frontend-ui';
import { createFileRoute } from '@tanstack/react-router';
import { UnauthenticatedError } from '../../utils/auth';
import {
  ensureOBSMinVersion,
  minVersionForMultiStreaming,
} from '../../utils/ensureOBSPresence';
import { CompositionsService } from '../../services/CompositionsService';
import { loadVideoComposition } from '../_authenticated.canvases/$canvasId/-videoCompositionCrudUtils';
import { DestinationsStorageApiClient } from '../../services/DestinationsApiClient';
import { promisefy } from '../../utils/promisefy';
import { getVideoCompositionDockingWidgetId, openVideoCompositionDockingWidget } from '../../utils/dockingWidgets';
import { UnsupportedOBSVersion } from '../../components/UnsupportedOBSVersion';
import { getIsKickEnabled } from '../../constants/common';
import { Analytics } from '../../services/AnalyticsService';
import { UnsupportedOBSVersionError } from '../../types/errors';

export const Route = createFileRoute('/_authenticated/')({
  component: () => <div></div>,
  pendingComponent: () => {
    return (
      <Box
        css={{
          size: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Text.Body>Awaiting 👍 from OBS...</Text.Body>
      </Box>
    );
  },
  beforeLoad: async ({ context: { authContextProvider } }) => {
    const { channel, isAnonymous } = await authContextProvider.getAuthContext();

    if (isAnonymous || !channel) {
      throw new UnauthenticatedError('User is not logged in');
    }
  },
  loader: async ({ context: { authContextProvider, serviceEnvironment } }) => {
    const { seLiveMultiStreamingMinVersionOk } = await ensureOBSMinVersion({
      minVersion: minVersionForMultiStreaming,
    });

    if (!seLiveMultiStreamingMinVersionOk) {
      throw new UnsupportedOBSVersionError();
    }

    const docks = await promisefy(window.host.getAllDockingWidgets)();
    const compositions = await CompositionsService.getActiveCompositions();
    const compositionsList = Object.values(compositions);

    /** NO promise.all because obs sometimes shits the bed with parallel API requests. IKR? 2024. */
    for (const composition of compositionsList) {
      if (composition.id === 'default') {
        continue;
      }

      await loadVideoComposition({ videoCompositionId: composition.id });

      const dockingWidgetId = getVideoCompositionDockingWidgetId({
        videoCompositionId: composition.id,
      });

      if (docks[dockingWidgetId]) {
        continue;
      }

      await openVideoCompositionDockingWidget({
        videoCompositionId: composition.id,
      });
    }

    const destinationsStorage = new DestinationsStorageApiClient({
      authContextProvider,
      serviceEnvironment,
    });

    await destinationsStorage.notifyOBSForActiveStreamingOutputs();

    const outputs = await promisefy(window.host.getAllStreamingOutputs)();

    Analytics.reportMultiStreamingEvent({
      name: 'outputs_dock_loaded',
      fields: [
        ['video_compositions_count', compositionsList.length], // Account for the default
        ['streaming_outputs_count', Object.values(outputs).length],
      ],
    });

    return {
      isKickEnabled: await getIsKickEnabled(),
    };
  },
  errorComponent: ({ error }) => {
    if (error instanceof UnauthenticatedError) {
      return <Unauthenticated />;
    }

    if (error instanceof UnsupportedOBSVersionError) {
      return <UnsupportedOBSVersion minVersion={minVersionForMultiStreaming} />;
    }

    return (
      <Box
        css={{
          size: '100%',
          display: 'flex',
          flexDirection: 'column',
          textAlign: 'center',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '1rem',
        }}
      >
        {error.message}
      </Box>
    );
  },
});

function Unauthenticated() {
  return (
    <Box
      css={{
        size: '100%',
        display: 'flex',
        flexDirection: 'column',
        textAlign: 'center',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '1rem',
      }}
    >
      <Text.Heading weight="bold" level="5">
        Unauthenticated
      </Text.Heading>
      <Text.Body css={{ color: '$textPrimary50' }}>You need to be logged in to use this feature.</Text.Body>
      <Text.Body css={{ color: '$textPrimary50' }}>
        If you did log in previously, please logout and login again.
      </Text.Body>
    </Box>
  );
}
